import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * Il ripiego di ULTIMA istanza: main.tsx lo monta anche nel ramo nudo, senza I18nextProvider né Suspense,
 * «so a crash here still surfaces something». Quindi non dipende dalla i18n: niente sospensione
 * (`useSuspense: false`), e il testo inglese letterale se la i18n non è pronta, se `t` lancia o se
 * rende la chiave. Se si rompe anche l'hook, `RipiegoStatico` mostra la versione senza hook.
 */
const INGLESE = { title: 'Something went wrong', reload: 'Reload' } as const;

function testoSicuro(t: (k: string) => unknown, pronta: boolean, chiave: string, inglese: string): string {
  if (!pronta) return inglese;
  try {
    const v = t(chiave);
    return typeof v === 'string' && v !== '' && v !== chiave ? v : inglese;
  } catch {
    return inglese;
  }
}

function Schermata({ error, title, reload }: { error: Error | null; title: string; reload: string }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
      <h2 className="text-xl font-bold text-destructive">{title}</h2>
      <p className="max-w-sm text-sm text-hint">{error?.message}</p>
      <button
        type="button"
        className="mt-2 rounded-lg bg-button px-4 py-2 text-button-text"
        onClick={() => window.location.reload()}
      >
        {reload}
      </button>
    </div>
  );
}

function ErroreInterfaccia({ error }: { error: Error | null }) {
  const { t, i18n } = useTranslation('general', { useSuspense: false });
  const pronta = Boolean(i18n?.isInitialized);
  return (
    <Schermata
      error={error}
      title={testoSicuro(t, pronta, 'errorBoundary.title', INGLESE.title)}
      reload={testoSicuro(t, pronta, 'chunkError.reload', INGLESE.reload)}
    />
  );
}

/** Se a rompersi è proprio l'hook della i18n, resta la schermata in inglese, senza hook. */
class RipiegoStatico extends Component<{ error: Error | null; children: ReactNode }, { rotto: boolean }> {
  state = { rotto: false };

  static getDerivedStateFromError(): { rotto: boolean } {
    return { rotto: true };
  }

  render() {
    if (this.state.rotto) return <Schermata error={this.props.error} title={INGLESE.title} reload={INGLESE.reload} />;
    return this.props.children;
  }
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
     
    console.error('[ErrorBoundary]', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          <RipiegoStatico error={this.state.error}>
            <ErroreInterfaccia error={this.state.error} />
          </RipiegoStatico>
        )
      );
    }
    return this.props.children;
  }
}
